import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import type { ReactElement } from 'react'
import { api, istApiError, istNichtAngemeldet } from '../api'
import { useSession } from '../auth'
import { Knopf } from '../components/Knopf'
import { fehlerText } from './login'

/** Die vier Ausgänge von `acceptInvite` (src/worker/auth/invites.ts, AcceptResult). */
export type EinladungErgebnis =
  | { status: 'member'; tenant_id: string; role: 'owner' | 'admin' }
  | { status: 'contractor_linked'; tenant_id: string; tenant_contractor_id: string; contractor_id: string }
  | { status: 'contractor_claimed'; tenant_id: string; tenant_contractor_id: string; contractor_id: string }
  | {
      status: 'choose_contractor'
      tenant_id: string
      tenant_contractor_id: string
      choices: Array<{ id: string; display_name: string; invoice_prefix: string }>
    }

export interface EinladungSuche {
  token?: string
}

const ROLLE: Readonly<Record<'owner' | 'admin', string>> = {
  owner: 'Inhaber',
  admin: 'Administrator',
}

export interface EinladungScreenProps {
  token: string | undefined
  /** Die Adresse der angemeldeten Person, sobald die Sitzungsabfrage sie kennt. */
  email: string | null
  /** Die Sitzungsabfrage läuft noch — es ist offen, ob jemand angemeldet ist. */
  laedt?: boolean
  /** Ein Fehler der Sitzungsabfrage, der **kein** 401 ist; Satz wörtlich vom Server. */
  sitzungsFehler?: string | null
}

/**
 * Drei Zustände, die nicht zusammenfallen dürfen: die Sitzung wird noch geprüft, die Prüfung ist
 * fehlgeschlagen, oder es ist niemand angemeldet. Nur der letzte darf zur Anmeldung schicken — wer
 * den Link aus der E-Mail öffnet und längst angemeldet ist, würde sonst während der laufenden
 * Abfrage aufgefordert, sich ein zweites Mal einen Code schicken zu lassen.
 */
export function EinladungScreen({
  token,
  email,
  laedt = false,
  sitzungsFehler = null,
}: EinladungScreenProps): ReactElement {
  const [ergebnis, setErgebnis] = useState<EinladungErgebnis | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [wahl, setWahl] = useState<string | null>(null)

  async function annehmen(contractorId: string | null): Promise<void> {
    if (token === undefined) return
    setFehler(null)
    setLaeuft(true)
    try {
      const koerper = contractorId === null ? { token } : { token, contractor_id: contractorId }
      setErgebnis(await api.post<EinladungErgebnis>('/api/auth/invite/accept', koerper))
    } catch (ausnahme) {
      setFehler(fehlerText(ausnahme))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="seite">
      <h1>Einladung</h1>

      {fehler !== null && (
        <p role="alert" className="mt-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">{fehler}</p>
      )}

      {token === undefined && (
        <p role="alert" className="mt-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          Dieser Einladungs-Link ist unvollständig. Bitte öffnen Sie den Link aus der E-Mail noch einmal.
        </p>
      )}

      {token !== undefined && laedt && (
        <p role="status" className="mt-6">Sitzung wird geprüft …</p>
      )}

      {token !== undefined && !laedt && sitzungsFehler !== null && (
        <p role="alert" className="mt-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          {sitzungsFehler}
        </p>
      )}

      {token !== undefined && !laedt && sitzungsFehler === null && email === null && (
        <>
          <p className="mt-6">
            Bitte melden Sie sich zuerst mit genau der E-Mail-Adresse an, an die diese Einladung ging.
            Danach kommen Sie automatisch hierher zurück.
          </p>
          <p className="mt-6">
            <a
              className="text-[var(--accent)] underline"
              href={`/login?weiter=${encodeURIComponent(`/einladung?token=${token}`)}`}
            >
              Jetzt anmelden
            </a>
          </p>
        </>
      )}

      {token !== undefined && email !== null && ergebnis === null && (
        <>
          <p className="mt-6">
            Sie sind als <strong>{email}</strong> angemeldet. Die Einladung wird nur angenommen, wenn sie
            an genau diese Adresse ausgestellt wurde.
          </p>
          <Knopf disabled={laeuft} onClick={() => void annehmen(null)}>Einladung annehmen</Knopf>
        </>
      )}

      {ergebnis !== null && ergebnis.status === 'member' && (
        <>
          <p role="status" className="mt-6">
            Sie gehören jetzt zum Team dieses Auftraggebers. Ihre Rolle: {ROLLE[ergebnis.role]}.
          </p>
          <p className="mt-6">
            <a className="text-[var(--accent)] underline" href={`/t/${ergebnis.tenant_id}/dashboard`}>Zum Dashboard</a>
          </p>
        </>
      )}

      {ergebnis !== null && (ergebnis.status === 'contractor_linked' || ergebnis.status === 'contractor_claimed') && (
        <>
          <p role="status" className="mt-6">
            {ergebnis.status === 'contractor_claimed'
              ? 'Dieser Empfänger gehört jetzt zu Ihrem Zugang. Ihre bisherigen Provisionen bleiben unverändert stehen.'
              : 'Ihr bestehendes Auftragnehmer-Profil ist jetzt mit diesem Auftraggeber verknüpft. Es entsteht keine zweite Rechnungsnummernserie.'}
          </p>
          <p className="mt-6">
            <a className="text-[var(--accent)] underline" href="/me/provisionen">Zu meinen Provisionen</a>
          </p>
        </>
      )}

      {ergebnis !== null && ergebnis.status === 'choose_contractor' && (
        <form
          className="mt-6"
          onSubmit={(nachricht) => {
            nachricht.preventDefault()
            if (wahl !== null) void annehmen(wahl)
          }}
        >
          <fieldset className="border border-[var(--rule-strong)] p-4">
            <legend className="px-1 text-sm text-[var(--ink-muted)]">
              Für welches Ihrer Profile gilt diese Einladung?
            </legend>
            {ergebnis.choices.map((auswahl) => (
              <label key={auswahl.id} className="mt-2 flex items-baseline gap-2">
                <input
                  type="radio"
                  name="contractor_id"
                  value={auswahl.id}
                  checked={wahl === auswahl.id}
                  onChange={() => setWahl(auswahl.id)}
                />
                <span>
                  {auswahl.display_name}{' '}
                  <span className="zahl text-[var(--ink-muted)]">
                    ({auswahl.invoice_prefix})
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <Knopf type="submit" disabled={laeuft || wahl === null}>Auswahl bestätigen</Knopf>
        </form>
      )}
    </div>
  )
}

function EinladungRoute(): ReactElement {
  const { token } = Route.useSearch()
  // Diese Seite ist die einzige des Auftragnehmer-Bereichs, die ohne Sitzung erreichbar sein muss:
  // wer den Link aus der E-Mail öffnet, ist meist noch nicht angemeldet. Deshalb `useSession()` mit
  // seiner Abfragehülle und nicht `useSitzung()`, das eine bestehende Sitzung voraussetzt.
  const sitzung = useSession()
  const email = sitzung.data?.user.email ?? null
  // Ein 401 heißt hier „noch nicht angemeldet" und ist der Normalfall dieser Seite; jeder andere
  // Fehler ist einer und bekommt den Satz des Servers, wörtlich (parts4/00-contract.md) — dieselbe
  // Unterscheidung wie in <RequireSession> (src/client/auth.tsx).
  const sitzungsFehler =
    sitzung.isError && !istNichtAngemeldet(sitzung.error)
      ? (istApiError(sitzung.error) ? sitzung.error.message : 'Die Sitzung konnte nicht geprüft werden.')
      : null
  return (
    <EinladungScreen token={token} email={email} laedt={sitzung.isPending} sitzungsFehler={sitzungsFehler} />
  )
}

export const Route = createFileRoute('/einladung')({
  validateSearch: (roh: Record<string, unknown>): EinladungSuche => ({
    token: typeof roh['token'] === 'string' && roh['token'] !== '' ? roh['token'] : undefined,
  }),
  component: EinladungRoute,
})
